import { useCallback, useEffect, useRef } from "react"

import { getPluginDetails } from "@fuse/api/pluginApi"
import { useRecoilState } from "recoil"

import { eventPluginsAtom, EventPluginState } from "@fuse/recoil/eventPlugins"

import { convertApiPluginToInternal } from "@fuse/utils/pluginConverters"
import { ApiPlugin } from "@fuse/utils/pluginConverters"

/**
 * Response type from getPluginDetails API
 */
interface PluginDetailsResponse {
  plugin: ApiPlugin
  configuration?: any
}

/**
 * Type for successful API response with plugin data
 */
interface SuccessfulPluginResponse {
  success: true
  data: PluginDetailsResponse
  plugin: ApiPlugin
  configuration?: any
}

/**
 * Type for error API response
 */
interface ErrorPluginResponse {
  success: false
  error: string
  isAuthError?: boolean
}

/**
 * Union type for all possible API responses
 */
type PluginApiResponse = SuccessfulPluginResponse | ErrorPluginResponse

/**
 * Type guard to check if response has plugin data
 */
function hasPluginData(response: any): response is SuccessfulPluginResponse {
  return response.success && response.data && response.data.plugin
}

/**
 * Hook for loading and managing plugins in events
 */
export function useEventPlugins() {
  const [eventPlugins, setEventPlugins] = useRecoilState(eventPluginsAtom)

  // Track state updates
  const stateUpdateCount = useRef(0)
  const prevEventPluginsRef = useRef(eventPlugins)

  useEffect(() => {
    const changes = Object.entries(eventPlugins.events).reduce(
      (acc, [eventId, eventState]) => {
        const prevEventState = prevEventPluginsRef.current.events[eventId]
        if (JSON.stringify(prevEventState) !== JSON.stringify(eventState)) {
          acc[eventId] = {
            from: prevEventState,
            to: eventState,
          }
        }
        return acc
      },
      {},
    )

    if (Object.keys(changes).length > 0) {
      stateUpdateCount.current++
      console.log("[PLUGIN] useEventPlugins state updated:", {
        updateCount: stateUpdateCount.current,
        changedEvents: Object.keys(changes),
        changes,
        timestamp: new Date().toISOString(),
      })
    }

    prevEventPluginsRef.current = eventPlugins
  }, [eventPlugins])

  /**
   * Load a plugin for an event
   * @param eventId - The event ID
   * @param pluginId - The plugin ID
   * @returns Promise with success status
   */
  const loadEventPlugin = useCallback(
    async (eventId: string, pluginId: string) => {
      console.log("[PLUGIN] useEventPlugins.loadEventPlugin called:", {
        eventId,
        pluginId,
        currentState: eventPlugins.events[eventId],
        updateCount: stateUpdateCount.current,
        timestamp: new Date().toISOString(),
        stackTrace: new Error().stack?.split("\n").slice(1, 5).join("\n"), // Show call stack
      })

      try {
        console.log(
          `[PLUGIN] useEventPlugins loading plugin ${pluginId} for event ${eventId}`,
        )

        // Check if plugin is already loaded and fresh
        const eventState = eventPlugins.events[eventId]
        const lastUpdated = eventPlugins.lastUpdated[eventId] || 0
        const now = Date.now()
        const cacheAge = now - lastUpdated

        // Use cached version if available and less than 5 minutes old
        if (eventState?.plugins[pluginId] && cacheAge < 5 * 60 * 1000) {
          console.log(
            `[PLUGIN] useEventPlugins using cached plugin (age: ${cacheAge}ms)`,
            { eventId, pluginId },
          )
          return { success: true, plugin: eventState.plugins[pluginId] }
        }

        // Mark plugin as loading
        setEventPlugins((prev) => {
          console.log("[PLUGIN] useEventPlugins setting loading state:", {
            eventId,
            pluginId,
            updateCount: stateUpdateCount.current,
            timestamp: new Date().toISOString(),
          })

          const currentEventState = prev.events[eventId] || {
            plugins: {},
            configurations: {},
            isLoading: true,
            hasError: false,
            pluginErrors: {},
          }

          return {
            ...prev,
            events: {
              ...prev.events,
              [eventId]: {
                ...currentEventState,
                isLoading: true,
                hasError: false,
              },
            },
          }
        })

        // Fetch from API
        console.log(
          `[PLUGIN] useEventPlugins calling getPluginDetails API for ${pluginId}`,
          {
            eventId,
            pluginId,
            apiCallTimestamp: new Date().toISOString(),
          },
        )

        const result = await getPluginDetails({
          pluginId,
          eventId,
        })

        console.log(
          `[PLUGIN] useEventPlugins getPluginDetails API response for ${pluginId}:`,
          {
            success: result.success,
            hasData: !!result.data,
            error: result.error,
            eventId,
            pluginId,
            responseTimestamp: new Date().toISOString(),
          },
        )

        if (!result.success) {
          console.error(
            `[PLUGIN] useEventPlugins failed to load plugin ${pluginId}:`,
            result.error,
          )

          // Update state with error
          setEventPlugins((prev) => {
            const currentEventState = prev.events[eventId] || {
              plugins: {},
              configurations: {},
              isLoading: false,
              hasError: true,
              errorMessage: result.error,
              pluginErrors: {},
            }

            return {
              ...prev,
              events: {
                ...prev.events,
                [eventId]: {
                  ...currentEventState,
                  isLoading: false,
                  hasError: true,
                  errorMessage: result.error,
                },
              },
            }
          })

          return { success: false, error: result.error }
        }

        // Convert API response to internal format
        const responseData = result.data as PluginDetailsResponse
        const apiPlugin = responseData.plugin
        const configuration = responseData.configuration

        if (!apiPlugin) {
          console.error(
            `[PLUGIN] useEventPlugins plugin ${pluginId} not found in response`,
          )
          return { success: false, error: "Plugin not found in response" }
        }

        const plugin = convertApiPluginToInternal(apiPlugin)

        console.log(
          `[PLUGIN] useEventPlugins successfully converted plugin ${pluginId}`,
          {
            pluginId: plugin.id,
            hasManifest: !!plugin.manifest,
            hasCode: !!plugin.code,
            eventId,
          },
        )

        // Update state with loaded plugin
        setEventPlugins((prev) => {
          console.log("[PLUGIN] useEventPlugins setting loaded plugin state:", {
            eventId,
            pluginId,
            updateCount: stateUpdateCount.current,
            timestamp: new Date().toISOString(),
          })

          const currentEventState = prev.events[eventId] || {
            plugins: {},
            configurations: {},
            isLoading: false,
            hasError: false,
            pluginErrors: {},
          }

          return {
            ...prev,
            events: {
              ...prev.events,
              [eventId]: {
                ...currentEventState,
                plugins: {
                  ...currentEventState.plugins,
                  [pluginId]: plugin,
                },
                configurations: {
                  ...currentEventState.configurations,
                  [pluginId]: configuration || {},
                },
                isLoading: false,
                hasError: false,
              },
            },
            lastUpdated: {
              ...prev.lastUpdated,
              [eventId]: Date.now(),
            },
          }
        })

        console.log(
          `[PLUGIN] useEventPlugins Successfully loaded plugin ${pluginId}`,
        )
        return { success: true, plugin }
      } catch (error) {
        console.error(
          `[PLUGIN] useEventPlugins Error loading plugin ${pluginId}:`,
          error,
        )

        // Update state with error
        setEventPlugins((prev) => {
          const currentEventState = prev.events[eventId] || {
            plugins: {},
            configurations: {},
            isLoading: false,
            hasError: true,
            errorMessage:
              error instanceof Error ? error.message : "Unknown error",
          }

          return {
            ...prev,
            events: {
              ...prev.events,
              [eventId]: {
                ...currentEventState,
                isLoading: false,
                hasError: true,
                errorMessage:
                  error instanceof Error ? error.message : String(error),
                pluginErrors: prev.events[eventId]?.pluginErrors || {},
              },
            },
          }
        })

        return {
          success: false,
          error: error instanceof Error ? error.message : "Unknown error",
        }
      }
    },
    [eventPlugins, setEventPlugins],
  )

  /**
   * Load multiple plugins for an event
   * @param eventId - The event ID
   * @param pluginIds - Array of plugin IDs to load
   * @returns Promise with success status
   */
  const loadEventPlugins = useCallback(
    async (eventId: string, pluginIds: string[]) => {
      console.log("[PLUGIN] useEventPlugins.loadEventPlugins called:", {
        eventId,
        pluginIds,
        pluginCount: pluginIds.length,
        currentState: {
          hasEventState: !!eventPlugins.events[eventId],
          isLoading: eventPlugins.events[eventId]?.isLoading,
          loadedPlugins: Object.keys(
            eventPlugins.events[eventId]?.plugins || {},
          ),
          loadedPluginsCount: Object.keys(
            eventPlugins.events[eventId]?.plugins || {},
          ).length,
        },
        updateCount: stateUpdateCount.current,
        timestamp: new Date().toISOString(),
        stackTrace: new Error().stack?.split("\n").slice(1, 5).join("\n"), // Show call stack
      })

      try {
        // Set loading state
        setEventPlugins((prev) => {
          console.log("[PLUGIN] useEventPlugins setting bulk loading state:", {
            eventId,
            pluginIds,
            pluginCount: pluginIds.length,
            updateCount: stateUpdateCount.current,
            timestamp: new Date().toISOString(),
          })

          return {
            ...prev,
            events: {
              ...prev.events,
              [eventId]: {
                plugins: prev.events[eventId]?.plugins || {},
                configurations: prev.events[eventId]?.configurations || {},
                isLoading: true,
                hasError: false,
                errorMessage: "",
                pluginErrors: prev.events[eventId]?.pluginErrors || {},
              },
            },
          }
        })

        // Load plugins in parallel
        console.log(
          `[PLUGIN] useEventPlugins starting parallel API calls for ${pluginIds.length} plugins:`,
          {
            eventId,
            pluginIds,
            timestamp: new Date().toISOString(),
          },
        )

        const results = await Promise.all(
          pluginIds.map(async (pluginId, index) => {
            try {
              console.log(
                `[PLUGIN] useEventPlugins calling getPluginDetails API for plugin ${index + 1}/${pluginIds.length}:`,
                {
                  eventId,
                  pluginId,
                  index,
                  apiCallTimestamp: new Date().toISOString(),
                },
              )

              const result = await getPluginDetails({
                pluginId,
                eventId,
              })

              console.log(
                `[PLUGIN] useEventPlugins getPluginDetails API response for plugin ${index + 1}/${pluginIds.length}:`,
                {
                  pluginId,
                  success: result.success,
                  hasData: !!result.data,
                  error: result.error,
                  responseTimestamp: new Date().toISOString(),
                },
              )

              // Process successful results to include plugin data
              if (result.success && result.data) {
                const responseData = result.data as PluginDetailsResponse
                const convertedPlugin = convertApiPluginToInternal(
                  responseData.plugin,
                )

                console.log(
                  `[PLUGIN] useEventPlugins successfully converted plugin ${pluginId}:`,
                  {
                    pluginId: convertedPlugin.id,
                    hasManifest: !!convertedPlugin.manifest,
                    hasCode: !!convertedPlugin.code,
                  },
                )

                return {
                  success: true as const,
                  data: responseData,
                  plugin: convertedPlugin,
                  configuration: responseData.configuration || {},
                }
              }
              return {
                success: false as const,
                error: result.error || "Unknown error",
                isAuthError: result.isAuthError,
              }
            } catch (error) {
              console.error(
                `[PLUGIN] useEventPlugins error loading individual plugin ${pluginId}:`,
                error,
              )

              if (error instanceof Error && error.name === "AbortError") {
                throw error
              }
              return {
                success: false,
                error: error instanceof Error ? error.message : String(error),
              }
            }
          }),
        )

        // Update final state with the loaded plugins
        const hasErrors = results.some((r) => !r.success)
        const successfulResults = results.filter((r) => hasPluginData(r))

        console.log("[PLUGIN] useEventPlugins completed bulk plugin loading:", {
          eventId,
          totalRequested: pluginIds.length,
          successfulCount: successfulResults.length,
          hasErrors,
          results: results.map((r, index) => ({
            pluginId: pluginIds[index],
            success: r.success,
            error: r.error,
            hasPlugin: !!(r as any).plugin,
          })),
          timestamp: new Date().toISOString(),
        })

        setEventPlugins((prev) => {
          const newPlugins = {}
          const newConfigurations = {}
          const newPluginErrors = {}

          // Process results into the state
          results.forEach((result, index) => {
            const pluginId = pluginIds[index]
            if (hasPluginData(result)) {
              newPlugins[pluginId] = result.plugin
              newConfigurations[pluginId] = result.configuration

              console.log(
                `[PLUGIN] useEventPlugins adding plugin ${pluginId} to state`,
                {
                  pluginId,
                  eventId,
                },
              )
            } else if (!result.success) {
              // Track error per plugin
              newPluginErrors[pluginId] = result.error || "Unknown error"
              console.log(
                `[PLUGIN] useEventPlugins tracking error for plugin ${pluginId}:`,
                {
                  pluginId,
                  error: result.error,
                  eventId,
                },
              )
            }
          })

          // Remove errors for successfully loaded plugins
          const prevPluginErrors = {
            ...(prev.events[eventId]?.pluginErrors || {}),
          }
          Object.keys(newPlugins).forEach((pluginId) => {
            delete prevPluginErrors[pluginId]
          })

          console.log(`[PLUGIN] useEventPlugins updating final state:`, {
            eventId,
            newPluginsCount: Object.keys(newPlugins).length,
            newPluginIds: Object.keys(newPlugins),
            newErrorsCount: Object.keys(newPluginErrors).length,
            newErrorIds: Object.keys(newPluginErrors),
            isLoading: false,
            hasErrors,
          })

          return {
            ...prev,
            events: {
              ...prev.events,
              [eventId]: {
                plugins: {
                  ...(prev.events[eventId]?.plugins || {}),
                  ...newPlugins,
                },
                configurations: {
                  ...(prev.events[eventId]?.configurations || {}),
                  ...newConfigurations,
                },
                isLoading: false,
                hasError: hasErrors,
                errorMessage: results.find((r) => !r.success)?.error || "",
                pluginErrors: {
                  ...prevPluginErrors,
                  ...newPluginErrors,
                },
              },
            },
            lastUpdated: {
              ...prev.lastUpdated,
              [eventId]: Date.now(),
            },
          }
        })
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          console.log(
            `[PLUGIN] useEventPlugins loadEventPlugins aborted for event ${eventId}`,
          )
          return
        }

        console.error("[PLUGIN] useEventPlugins critical error:", {
          error,
          eventId,
          pluginIds,
          stack: error instanceof Error ? error.stack : undefined,
        })

        setEventPlugins((prev) => ({
          ...prev,
          events: {
            ...prev.events,
            [eventId]: {
              plugins: prev.events[eventId]?.plugins || {},
              configurations: prev.events[eventId]?.configurations || {},
              isLoading: false,
              hasError: true,
              errorMessage:
                error instanceof Error ? error.message : String(error),
              pluginErrors: prev.events[eventId]?.pluginErrors || {},
            },
          },
        }))
      }
    },
    [], // Empty dependency array since we use functional updates
  )

  /**
   * Clear plugins for an event
   * @param eventId - The event ID to clear plugins for
   */
  const clearEventPlugins = useCallback(
    (eventId: string) => {
      setEventPlugins((prev) => {
        const { [eventId]: _, ...restEvents } = prev.events
        const { [eventId]: __, ...restLastUpdated } = prev.lastUpdated

        return {
          events: restEvents,
          lastUpdated: restLastUpdated,
        }
      })
    },
    [setEventPlugins],
  )

  return {
    eventPlugins,
    loadEventPlugin,
    loadEventPlugins,
    clearEventPlugins,
  }
}
